'use client';

import Script from 'next/script';
import { ReactNode, createContext, useCallback, useContext, useState } from 'react';

interface CookiebotProviderProps {
  children: ReactNode;
}

interface CookiebotCallbacks {
  show: () => void;
  hide: () => void;
  renew: () => void;
  withdraw: () => void;
}

export const CookiebotScript = () => {
  const {
    setCallbacks,
    setAreMarketingCookiesAllowed,
    setAreNecessaryCookiesAllowed,
    setArePreferencesCookiesAllowed,
    setAreStatisticsCookiesAllowed,
  } = useContext(CookiebotContext);

  const handleCookiebotEvent = useCallback(() => {
    if (!window.CookieConsent) {
      return;
    }

    const isTrackingDisabledByBrowser = window.CookieConsent.doNotTrack;
    const areNecessaryCookiesAccepted = window.CookieConsent.consent.necessary;
    const areMarketingCookiesAccepted = window.CookieConsent.consent.marketing;
    const arePreferencesCookiesAccepted = window.CookieConsent.consent.preferences;
    const areStatisticsCookiesAccepted = window.CookieConsent.consent.statistics;

    setAreNecessaryCookiesAllowed(!isTrackingDisabledByBrowser && areNecessaryCookiesAccepted);
    setAreMarketingCookiesAllowed(!isTrackingDisabledByBrowser && areMarketingCookiesAccepted);
    setArePreferencesCookiesAllowed(!isTrackingDisabledByBrowser && arePreferencesCookiesAccepted);
    setAreStatisticsCookiesAllowed(!isTrackingDisabledByBrowser && areStatisticsCookiesAccepted);

    setCallbacks({
      show: window.CookieConsent.show,
      hide: window.CookieConsent.hide,
      renew: window.CookieConsent.renew,
      withdraw: window.CookieConsent.withdraw,
    });
  }, [
    setCallbacks,
    setAreNecessaryCookiesAllowed,
    setAreMarketingCookiesAllowed,
    setArePreferencesCookiesAllowed,
    setAreStatisticsCookiesAllowed,
  ]);

  const handleScriptLoad = useCallback(() => {
    if (!window.CookieConsent) {
      return;
    }

    setCallbacks({
      show: window.CookieConsent.show,
      hide: window.CookieConsent.hide,
      renew: window.CookieConsent.renew,
      withdraw: window.CookieConsent.withdraw,
    });

    window.addEventListener('CookiebotOnAccept', handleCookiebotEvent);
    window.addEventListener('CookiebotOnDecline', handleCookiebotEvent);
  }, [setCallbacks, handleCookiebotEvent]);

  return (
    <Script
      id="Cookiebot"
      src="https://consent.cookiebot.com/uc.js"
      data-cbid={process.env.NEXT_PUBLIC_COOKIEBOT_ID}
      data-blockingmode="manual" // důležité: manuální režim
      type="text/javascript"
      strategy="afterInteractive"
      async
      onLoad={handleScriptLoad}
    />
  );
};

type Dispatch<A> = (value: A) => void;
type SetStateAction<S> = S | ((prevState: S) => S);

export const CookiebotContext = createContext<{
  callbacks: CookiebotCallbacks | null;
  setCallbacks: Dispatch<SetStateAction<CookiebotCallbacks | null>>;
  areNecessaryCookiesAllowed: boolean;
  setAreNecessaryCookiesAllowed: Dispatch<SetStateAction<boolean>>;
  areMarketingCookiesAllowed: boolean;
  setAreMarketingCookiesAllowed: Dispatch<SetStateAction<boolean>>;
  arePreferencesCookiesAllowed: boolean;
  setArePreferencesCookiesAllowed: Dispatch<SetStateAction<boolean>>;
  areStatisticsCookiesAllowed: boolean;
  setAreStatisticsCookiesAllowed: Dispatch<SetStateAction<boolean>>;
}>({
  callbacks: null,
  setCallbacks: () => null,
  areNecessaryCookiesAllowed: false,
  setAreNecessaryCookiesAllowed: () => {},
  areMarketingCookiesAllowed: false,
  setAreMarketingCookiesAllowed: () => {},
  arePreferencesCookiesAllowed: false,
  setArePreferencesCookiesAllowed: () => {},
  areStatisticsCookiesAllowed: false,
  setAreStatisticsCookiesAllowed: () => {},
});

export const CookiebotProvider = ({ children }: CookiebotProviderProps) => {
  const [callbacks, setCallbacks] = useState<CookiebotCallbacks | null>(null);
  const [areNecessaryCookiesAllowed, setAreNecessaryCookiesAllowed] = useState<boolean>(false);
  const [arePreferencesCookiesAllowed, setArePreferencesCookiesAllowed] = useState<boolean>(false);
  const [areMarketingCookiesAllowed, setAreMarketingCookiesAllowed] = useState<boolean>(false);
  const [areStatisticsCookiesAllowed, setAreStatisticsCookiesAllowed] = useState<boolean>(false);

  return (
    <CookiebotContext.Provider
      value={{
        callbacks,
        setCallbacks,
        areNecessaryCookiesAllowed,
        setAreNecessaryCookiesAllowed,
        areMarketingCookiesAllowed,
        setAreMarketingCookiesAllowed,
        arePreferencesCookiesAllowed,
        setArePreferencesCookiesAllowed,
        areStatisticsCookiesAllowed,
        setAreStatisticsCookiesAllowed,
      }}
    >
      {children}
    </CookiebotContext.Provider>
  );
};
